import React, { useState, useEffect } from 'react';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  LineChart, Line, AreaChart, Area
} from 'recharts';
import { 
  Users, DollarSign, BookOpen, GraduationCap, PackageIcon,
  Menu, X
} from 'lucide-react';
import Sidebar from '../../component/includes/sidebar';
import { productService } from '../../services/product/product.service';
import { articleService } from '../../services/Artikel';
import { visitorService } from '../../services/visitor';
import { userService } from '../../services/user';

const Dashboard = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [productCount, setProductCount] = useState(0);
  const [articleCount, setArticleCount] = useState(0);
  const [visitorCount, setVisitorCount] = useState(0);
  const [userCount, setUserCount] = useState(0);
  
  // Sample data for charts (enhanced with visitors)
  const monthlyRevenue = [
    { name: 'Jan', amount: 4500 },
    { name: 'Feb', amount: 5200 },
    { name: 'Mar', amount: 4800 },
    { name: 'Apr', amount: 6300 },
    { name: 'May', amount: 5900 },
    { name: 'Jun', amount: 7500 }
  ];

  const userStats = [
    { month: 'Jan', active: 500, new: 120 },
    { month: 'Feb', active: 580, new: 150 },
    { month: 'Mar', active: 620, new: 180 },
    { month: 'Apr', active: 700, new: 190 },
    { month: 'May', active: 780, new: 220 },
    { month: 'Jun', active: 850, new: 250 }
  ];

  const [visitorStats, setVisitorStats] = useState([
    { month: 'Jan', visitors: 1200 },
    { month: 'Feb', visitors: 1500 },
    { month: 'Mar', visitors: 1350 },
    { month: 'Apr', visitors: 1650 },
    { month: 'May', visitors: 1800 },
    { month: 'Jun', visitors: 2000 }
  ]);

  const trainingData = [
    { month: 'Jan', participants: 150 },
    { month: 'Feb', participants: 220 },
    { month: 'Mar', participants: 180 },
    { month: 'Apr', participants: 280 },
    { month: 'May', participants: 260 },
    { month: 'Jun', participants: 300 }
  ];

  // Fetch product, article, and visitor counts
  useEffect(() => {
    const fetchCounts = async () => {
      try {
        // Fetch product count
        const products = await productService.getAllProducts();
        setProductCount(products.length);

        // Fetch article count
        const articles = await articleService.getArticles();
        setArticleCount(articles.length);

        // Fetch visitor count
        const visitors = await visitorService.getVisitorCount();
        setVisitorCount(visitors);

        // Fetch user count
        const users = await userService.getAllUsers();
        setUserCount(users.length);
      } catch (error) {
        console.error('Failed to fetch counts', error);
      }
    };

    fetchCounts();
  }, []);

  const statCards = [
    {
      title: 'Total Pengguna',
      value: userCount.toString(),
      icon: Users,
      colorClass: 'bg-blue-50 text-blue-500'
    },
    {
      title: 'Pengunjung',
      value: visitorCount.toString(),
      icon: GraduationCap,
      colorClass: 'bg-orange-50 text-orange-500'
    },
    {
      title: 'Total Artikel',
      value: articleCount.toString(),
      icon: BookOpen,
      colorClass: 'bg-indigo-50 text-indigo-500'
    },
    {
      title: 'Total Produk',
      value: productCount.toString(),
      icon: PackageIcon,
      colorClass: 'bg-purple-50 text-purple-500'
    }
  ];

  return (
    <div className="min-h-screen">
      <div className="flex flex-col md:flex-row mt-10">
        <Sidebar />

        <main className="flex-1 p-4 md:p-6 mt-4 md:mt-10">
          <div className="max-w-7xl mx-auto">
            {/* Header */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
              <h1 className="text-2xl font-semibold text-gray-800">Dashboard Overview</h1>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6 mb-8">
              {statCards.map((stat, index) => (
                <div key={index} className="bg-white rounded-xl shadow-sm p-4 lg:p-6">
                  <div className="flex items-center justify-between">
                    <div className="space-y-1">
                      <p className="text-sm text-gray-500">{stat.title}</p>
                      <h3 className="text-xl lg:text-2xl font-semibold text-gray-800">
                        {stat.value}
                      </h3>
                    </div>
                    <div className={`p-3 rounded-lg ${stat.colorClass}`}>
                      <stat.icon className="w-6 h-6" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default Dashboard;

